import ShippingPrivacy from '@/components/ShippingPrivacy';
import useGenerateEstString from '@/hooks/useGenerateEstString';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IRule } from '@/types/newSetup.type';
import { InlineStack } from '@shopify/polaris';
import parse from 'html-react-parser';
import { memo, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { DeliveryMethodRadioStyled } from './styled';
interface IProps {
  id: number;
  name: string | React.ReactNode;
  estText: string;
  orderToShipDays: number;
  preDays: number;
  shipToReceiveDays: number;
  estDays: number;
  checked: boolean;
  onChange: (e: boolean) => void;
  showRadioInput?: boolean;
  isShowDeliveryMethodName?: boolean;
  country?: string;
  flag?: string;
  zipcode?: string;
  privacyPageUrl: string;
  privacyPageText: string;
  privacyMore: string;
  rule?: IRule;
  isUsePreOrderDate?: boolean;
  preOrderDate?: string;
}

const DeliveryMethodRadio = memo(({ showRadioInput = true, ...props }: IProps) => {
  const generateEstString = useGenerateEstString();
  const {
    enable_eta_information,
    border_color: borderColor,
    background_color: backgroundColor,
    text_color: textColor,
    text_size: textSize,
  } = useSelector(dataSettingsSelector);

  const styles = useMemo(
    () => ({
      checked: {
        border: `2px solid ${borderColor}`,
        backgroundColor,
        fontSize: `${textSize}px`,
        color: textColor,
      },
      unchecked: {
        border: `2px solid ${borderColor}33`,
        backgroundColor: `${backgroundColor}33`,
        fontSize: `${textSize}px`,
        color: textColor,
      },
    }),
    [borderColor, backgroundColor, textSize, textColor],
  );

  const radioClassName = useMemo(
    () =>
      `ot-estimated-shipping-item-shipping-method ${
        showRadioInput ? '' : 'ot-estimated-shipping-item-shipping-method-hide-radio'
      }`,
    [showRadioInput],
  );

  const parsedName = useMemo(() => (typeof props.name === 'string' ? parse(props.name) : ''), [props.name]);

  const renderEstString = useMemo(
    () =>
      parse(
        generateEstString(
          props.estText,
          props.orderToShipDays,
          props.preDays,
          props.shipToReceiveDays,
          props.estDays,
          false,
          props.country,
          props.flag,
          props.preOrderDate,
          props.isUsePreOrderDate,
          props.zipcode,
        ),
      ),
    [
      generateEstString,
      props.estText,
      props.orderToShipDays,
      props.preDays,
      props.shipToReceiveDays,
      props.estDays,
      props.country,
      props.flag,
      props.preOrderDate,
      props.isUsePreOrderDate,
      props.zipcode,
    ],
  );

  const handleChange = useCallback(() => {
    props.onChange(!props.checked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.checked, props.onChange]);

  if (!enable_eta_information) return null;

  return (
    <DeliveryMethodRadioStyled
      style={props.checked ? styles.checked : styles.unchecked}
      onClick={handleChange}
      className={radioClassName}
    >
      <div className="ot-estimated-shipping-methods-checkbox-container">
        <input
          className="ot-estimated-shipping-item-radio ot-estimated-shipping-shipping-methods-select-item"
          key={props.id}
          id={props.id.toString()}
          type={'radio'}
          name={'check_shipping_method'}
          checked={props.checked}
          style={{ accentColor: borderColor, backgroundColor: backgroundColor }}
          readOnly
        />
        {showRadioInput ? <label htmlFor={props.id.toString()}></label> : null}
      </div>

      <div className="ot-estimated-shipping-estimated-text-container">
        {props.isShowDeliveryMethodName ? (
          <div className="ot-estimated-shipping-title-shipping">
            <span className="ot-estimated-shipping-name-shipping">{parsedName}</span>
          </div>
        ) : null}
        <div className="ot-estimated-shipping-text-shipping">{renderEstString}</div>
        <InlineStack gap={'100'}>
          <ShippingPrivacy rule={props.rule} />
        </InlineStack>
      </div>
    </DeliveryMethodRadioStyled>
  );
});

export default DeliveryMethodRadio;
